import _ from "lodash"
import { Letter, Word } from "./game"
import { MAX_WORD_LENGTH } from './consts'
import wordlist from "../data/words"
import { sample, shuffle } from "../utils/rand"

class ReversalError extends Error {
    constructor(message: string) {
        super(message)
        this.name = "ReversalError"
    }
}

export interface Mutator {
    name: string,
    apply: (word:Word)=>Word, // Changes the string in some REVERSABLE fashion. ALWAYS REMEMEBER TO CLONE
    reverse: (word:Word)=>Word // Reverse the change, often adding information. ALWAYS REMEMEBER TO CLONE
    weight: number, // The weight when selecting mutators for the solution
    redHerringWeight: number // The weight when selecting incorrect mutators
    maxReversalLength: (inputLength: number) => number // Returns the highest number of letters that can be reversed
    additive: boolean // Applying this adds information, each puzzle requires at least one additive mutator
}


const VOWELS = ['a', 'e', 'i', 'o', 'u']
export const LETTERS = 'abcdefghijklmnopqrstuvwxyz'.split('')
export const COMMON_LETTERS =  'eariotnslcudpmhgb'.split('')
const COMMON_FRAGMENTS = ['ing', 'ion', 'ed', 'sh', 'th', 'ity', 'ness', 'est', 'ment', 'gh', 'ough', 'tion', 'ful', 'less', 'ly', 'er',  'ive', 'able', 'ible', 'ize']

function allCombinations<T>(list: T[]){
    const result = []
    for(let i = 0; i < list.length; i++){
        for(let j = i+1; j < list.length; j++){
            if (i !== j) result.push([list[i], list[j]])
        }
    }
    return result
}
function weighted_random<T>(items:T[], weights:number[]): T {
    let i;

    for (i = 1; i < weights.length; i++)
        weights[i] += weights[i - 1];
    
    const random = Math.random() * weights[weights.length - 1];
    
    for (i = 0; i < weights.length; i++)
        if (weights[i] > random)
            break;
    
    return items[i];
}


const REVERSE: Mutator = {
    name: "Reverse letters",
    weight: 0.05, //I'm so bored of this one
    redHerringWeight: 1,
    apply: (w)=>({
        ...w,
        value: [...w.value].reverse()
    }),
    reverse: (w)=>({
        ...w,
        value: [...w.value].reverse()
    }),
    maxReversalLength: (inputLength)=>inputLength,
    additive: false
}


const DELETE_LETTERS:Mutator[] = COMMON_LETTERS.map(letter=>({
    name: `Remove ${letter}'s`,
    weight: 1/COMMON_LETTERS.length,
    redHerringWeight: 1/COMMON_LETTERS.length,
    apply: (w)=>{
        const newValue = w.value.filter(l=>l.value !== letter)
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const randomIndex = Math.floor(Math.random()*w.value.length)
        const newValue = [...w.value]
        newValue.splice(randomIndex, 0, {value:letter, key:-Math.random(), correctValue:null})
        return {
            ...w,
            value: newValue
        }
    },
    maxReversalLength: (inputLength)=>inputLength+1,
    additive: false
}))

const DELETE_FIRST_LETTER:Mutator = {
    name: `Remove first letter`,
    weight: .25,
    redHerringWeight: 1,
    apply: (w)=>({
        ...w,
        value: [...w.value].slice(1)
    }),
    reverse: (w)=>{
        const newLetter = sample(COMMON_LETTERS)!
        return {
            ...w,
            value: [{value:newLetter, key:-Math.random(), correctValue:null}, ...w.value]
        }
    },
    maxReversalLength: (inputLength)=>inputLength+1,
    additive: false
}

export const DELETE_LAST_LETTER:Mutator = {
    name: `Remove last letter`,
    weight: 0.2,
    redHerringWeight: 1,
    apply: (w)=>({
        ...w,
        value: [...w.value].slice(0, -1)
    }),
    reverse: (w)=>{
        const newLetter = sample(COMMON_LETTERS)!
        return {
            ...w,
            value: [...w.value, {value:newLetter, key:-Math.random(), correctValue:null}]
        }
    },
    maxReversalLength: (inputLength)=>inputLength+1,
    additive: false
}

export const DELETE_FIRST_VOWEL:Mutator = {
    name: `Remove first vowel`,
    weight: 0.1,
    redHerringWeight: 1,
    apply: (w)=>{
        let newValue = [...w.value]
        const firstVowelIndex = newValue.findIndex(l=>VOWELS.includes(l.value))
        if(firstVowelIndex >= 0){
            newValue = newValue.slice(0, firstVowelIndex).concat(newValue.slice(firstVowelIndex+1))
        }
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const firstVowelIndex = w.value.findIndex(l=>VOWELS.includes(l.value))
        const newVowel = sample(VOWELS)!
        const newVowelIndex = Math.floor(Math.random()*firstVowelIndex) // Has to come before the first vowel
        const newValue = [
            ...w.value.slice(0, newVowelIndex), 
            {value:newVowel, key:-Math.random(), correctValue:null}, 
            ...w.value.slice(newVowelIndex)
        ]
        return {
            ...w,
            value: newValue
        }
    },
    maxReversalLength: (inputLength)=>inputLength+1,
    additive: false
}

const DELETE_COMMON_FRAGMENTS:Mutator[] = COMMON_FRAGMENTS.map(fragment=>({
    name: `Remove ${fragment}`,
    weight: 0.5/COMMON_FRAGMENTS.length,
    redHerringWeight: 0.1/COMMON_FRAGMENTS.length, // Increase this is we can find a way to make only mutators which change enough of the words to be red herrings
    apply: (w)=>{
        let newValue = w.value
        let fragmentIndex = 0
        while(fragmentIndex < newValue.length){
            if(newValue.slice(fragmentIndex, fragmentIndex+fragment.length).map(l=>l.value).join('') === fragment){
                newValue = newValue.slice(0, fragmentIndex).concat(newValue.slice(fragmentIndex+fragment.length))
            }
            fragmentIndex++
        }
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const randomIndex = Math.floor(Math.random()*w.value.length)
        const newValue = [...w.value]
        newValue.splice(randomIndex, 0, ...fragment.split('').map((value)=>({value, key:-Math.random(), correctValue:null})))
        return {
            ...w,
            value: newValue
        }
    },
    maxReversalLength: (inputLength)=>inputLength+fragment.length,
    additive: false
}))

export const ADD_COMMON_FRAGMENT_TO_END:Mutator[] = COMMON_FRAGMENTS.map(fragment=>({
    name: `Add ${fragment} to end`,
    weight: 10/COMMON_FRAGMENTS.length,
    redHerringWeight: 0.1/COMMON_FRAGMENTS.length,
    apply: (w)=>({
        ...w,
        value: [...w.value, ...fragment.split('').map((value,i)=>({value, key:`${ADD_COMMON_FRAGMENT_TO_END}${i}${fragment}`, correctValue:null}))]
    }),
    reverse: (w)=>{
        const lastFragment = w.value.slice(-fragment.length).map(l=>l.value).join('')
        if(lastFragment !== fragment){
            throw new ReversalError("Last fragment is not the same as the fragment to be added")
        }
        return {
            ...w,
            value: w.value.slice(0, -fragment.length)
        }
    },
    maxReversalLength: (inputLength)=>inputLength-fragment.length,
    additive: true
}))

const ADD_LETTER_AFTER_FIRST_VOWEL: Mutator[] = COMMON_LETTERS.map(letter=>({
    name: `Add ${letter} after first vowel`,
    weight: 20/COMMON_LETTERS.length,
    redHerringWeight: 1/COMMON_FRAGMENTS.length,
    apply: (w)=>{
        const newValue = [...w.value]
        const firstVowelIndex = newValue.findIndex(l=>VOWELS.includes(l.value))
        if(firstVowelIndex >= 0){
            newValue.splice(firstVowelIndex+1, 0, {value:letter, key:`${ADD_LETTER_AFTER_FIRST_VOWEL}${letter}`, correctValue:null})
        }
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        // Find the first vowel
        const firstVowelIndex = w.value.findIndex(l=>VOWELS.includes(l.value))
        if(firstVowelIndex >= 0){
            const letterAfterFirstVowel = w.value[firstVowelIndex+1]
            if(!letterAfterFirstVowel || letterAfterFirstVowel.value !== letter){
                throw new ReversalError("Letter after first vowel is not the same as the letter to be added")
            }
            const newValue = [...w.value]
            newValue.splice(firstVowelIndex+1, 1)
            return {
                ...w,
                value: newValue
            }
        }
        return w // No vowels, so no change
    },
    maxReversalLength: (inputLength)=>inputLength-1,
    additive: true
}))

const ADD_LETTER_TO_END: Mutator[] = COMMON_LETTERS.map(letter=>({
    name: `Add ${letter} to end`,
    weight: 20/COMMON_LETTERS.length,
    redHerringWeight: 1/COMMON_FRAGMENTS.length,
    apply: (w)=>({
        ...w,
        value: [...w.value, {value:letter, key:`${ADD_LETTER_TO_END}${letter}`, correctValue:null}]
    }),
    reverse: (w)=>{
        const lastLetter = w.value[w.value.length-1]
        if(lastLetter.value !== letter){
            throw new ReversalError("Last letter is not the same as the letter to be added")
        }
        return {
            ...w,
            value: w.value.slice(0, -1)
        }
    },
    maxReversalLength: (inputLength)=>inputLength-1,
    additive: true
}))

const ADD_LETTER_TO_START: Mutator[] = COMMON_LETTERS.map(letter=>({
    name: `Add ${letter} to start`,
    weight: 20/COMMON_LETTERS.length,
    redHerringWeight: 1/COMMON_FRAGMENTS.length,
    apply: (w)=>({
        ...w,
        value: [{value:letter, key:`${ADD_LETTER_TO_START}${letter}`, correctValue:null}, ...w.value]
    }),
    reverse: (w)=>{
        const firstLetter = w.value[0]
        if(firstLetter.value !== letter){
            throw new ReversalError("First letter is not the same as the letter to be added")
        }
        return {
            ...w,
            value: w.value.slice(1)
        }
    },
    maxReversalLength: (inputLength)=>inputLength-1,
    additive: true
}))

const SWAP_FIRST_AND_LAST: Mutator = {
    name: "Swap first and last letters",
    weight: 0.1,
    redHerringWeight: 0.1,
    apply: (w)=>{
        if(w.value.length < 2){
            return w
        }
        const newValue = [...w.value]
        const first = newValue[0]
        newValue[0] = newValue[newValue.length-1]
        newValue[newValue.length-1] = first
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>SWAP_FIRST_AND_LAST.apply(w),
    maxReversalLength: (inputLength)=>inputLength,
    additive: false
}

const REPLACE_VOWEL: Mutator[] = allCombinations(VOWELS).map(([from, to])=>({
    name: `Replace ${from}'s with ${to}'s`,
    weight: 2/(VOWELS.length * (VOWELS.length - 1)),
    redHerringWeight: 1/(VOWELS.length * (VOWELS.length - 1)),
    apply: (w)=>{
        const newValue = w.value.map(l=>l.value === from ? {value:to, key:l.key, correctValue:null} : l)
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const newValue = w.value.map(l=>l.value === to ? {value:from, key:l.key, correctValue:null} : l)
        return {
            ...w,
            value: newValue
        }
    },
    maxReversalLength: (inputLength)=>inputLength,
    additive: true // Adding a vowel adds information, even if another is removed
}))

export const DUPLICATE_FIRST_VOWEL: Mutator = {
    name: "Duplicate first vowel",
    weight: 0.8,
    redHerringWeight: 1,
    apply: (w)=>{
        const newValue = [...w.value]
        const firstVowelIndex = newValue.findIndex(l=>VOWELS.includes(l.value))
        if(firstVowelIndex < 0){
            return w
        }
        const newLetter = {
            value: newValue[firstVowelIndex].value,
            key: "DUPLICATE_FIRST_VOWEL",
            correctValue: null
        }
        if(firstVowelIndex >= 0){
            newValue.splice(firstVowelIndex+1, 0, newLetter)
        }
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const newValue = [...w.value]
        const firstVowelIndex = newValue.findIndex(l=>VOWELS.includes(l.value))
        if(firstVowelIndex >= 0){
            if(newValue[firstVowelIndex+1].value !== newValue[firstVowelIndex].value){
                throw new ReversalError("The duplicated vowel is not the same as the original")
            }
            newValue.splice(firstVowelIndex+1, 1)
        }
        return {
            ...w,
            value: newValue
        }
    },
    maxReversalLength: (inputLength)=>inputLength-1,
    additive: true
}

const CUT_IN_HALF: Mutator = {
    name: "Remove second half",
    weight: 1,
    redHerringWeight: 0.25,
    apply: (w)=>{
        let newValue = [...w.value]
        newValue = newValue.slice(0, Math.floor(newValue.length/2))
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const wordsWithDoubleLength = wordlist.filter(word=>word.length===w.value.length*2)
        const wordWithDoubleLength = sample(wordsWithDoubleLength)
        if(!wordWithDoubleLength){
            throw new ReversalError("No word with double length found")
        }
        const secondHalf = wordWithDoubleLength.split('').slice(Math.floor(wordWithDoubleLength.length/2))
        const newValue = [...w.value].concat(secondHalf.map((value)=>({value, key:-Math.random(), correctValue:null})))
        return {
            ...w,
            value: newValue
        }
    },
    maxReversalLength: (inputLength)=>inputLength * 2,
    additive: false
}

const REPLACE_ALL_VOWELS_WITH: Mutator[] = VOWELS.map(vowel=>({
    name: `Replace all vowels with ${vowel}`,
    weight: 10/VOWELS.length,
    redHerringWeight: 1/VOWELS.length,
    apply: (w)=>{
        const newValue = w.value.map(l=>VOWELS.includes(l.value) ? {value:vowel, key:l.key, correctValue:null} : l)
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const newValue = w.value.map(l=>l.value === vowel ? {value:sample(VOWELS)!, key:l.key, correctValue:null} : l)
        return {
            ...w,
            value: newValue
        }
    },
    maxReversalLength: (inputLength)=>inputLength,
    additive: true
}))

const REMOVE_LETTER_AT_INDEX: Mutator[] = ['second','third','fourth','fifth','sixth'].map((word, index)=>({
    name: `Remove ${word} letter`,
    weight: 0.3/MAX_WORD_LENGTH,
    redHerringWeight: 2/MAX_WORD_LENGTH,
    apply: (w)=>{
        const newValue = [...w.value]
        newValue.splice(index+1, 1)
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const newLetter = sample(COMMON_LETTERS)!
        const newValue = [...w.value]
        newValue.splice(index+1, 0, {value:newLetter, key:-Math.random(), correctValue:null})
        return {
            ...w,
            value: newValue
        }
    },
    maxReversalLength: (inputLength)=>inputLength+1,
    additive: false
}))

const ADD_LETTER_TO_MIDDLE: Mutator[] = COMMON_LETTERS.map(letter=>({
    name: `Add ${letter} to middle`,
    weight: 10/COMMON_LETTERS.length,
    redHerringWeight: 1/COMMON_FRAGMENTS.length,
    apply: (w)=>{
        const newValue = [...w.value]
        const index = Math.floor(newValue.length/2)
        newValue.splice(index, 0, {value:letter, key:`ADD_LETTER_TO_MIDDLE_${letter}`, correctValue:null})
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const middleIndex = Math.floor(w.value.length/2)
        const middleLetter = w.value[middleIndex]
        if(middleLetter.value !== letter){
            throw new ReversalError("Middle letter is not the same as the letter to be added")
        }
        const newValue = [...w.value]
        newValue.splice(middleIndex, 1)
        return {
            ...w,
            value: newValue
        }
    },
    maxReversalLength: (inputLength)=>inputLength-1,
    additive: true
}))

export const SORT_VOWELS: Mutator = {
    name: "Sort vowels",
    weight: 1,
    redHerringWeight: 2,
    apply: (w)=>{
        const vowelIndices = w.value.map((l, i)=>VOWELS.includes(l.value) ? i : -1).filter(i=>i >= 0)
        const wordWithoutVowels = w.value.filter(l=>!VOWELS.includes(l.value))
        const vowels:Letter[] = vowelIndices.map(i=>w.value[i])
        vowels.sort((a, b)=>a.value.localeCompare(b.value))
        const newValue = [...wordWithoutVowels]
        vowelIndices.forEach((index, i)=>{
            newValue.splice(index, 0, vowels[i])
        })
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        // Randomly shuffle the vowels
        const vowelIndices = w.value.map((l, i)=>VOWELS.includes(l.value) ? i : -1).filter(i=>i >= 0)
        const wordWithoutVowels = w.value.filter(l=>!VOWELS.includes(l.value))
        const vowels:Letter[] = vowelIndices.map(i=>w.value[i])
        const shuffledVowels = shuffle(vowels)
        const newValue = [...wordWithoutVowels]
        vowelIndices.forEach((index, i)=>{
            newValue.splice(index, 0, shuffledVowels[i])
        })
        return {
            ...w,
            value: newValue
        }
    },
    maxReversalLength: (inputLength)=>inputLength,
    additive: false
}

export const DUPLICATE_MIDDLE_LETTER: Mutator = {
    name: "Duplicate middle letter",
    weight: 3,
    redHerringWeight: 1,
    apply: (w)=>{
        const newValue = [...w.value]
        const middleIndex = Math.floor(newValue.length/2)
        const newLetter: Letter = {
            value: newValue[middleIndex].value,
            key: "DUPLICATE_MIDDLE_LETTER",
            correctValue: null
        }
        newValue.splice(middleIndex+1, 0, newLetter)
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const newValue = [...w.value]
        const middleIndex = Math.floor(newValue.length/2) -1
        if(newValue[middleIndex].value !== newValue[middleIndex+1].value){
            throw new ReversalError("Middle letter is not the same as the duplicated letter")
        }
        newValue.splice(middleIndex, 1)
        return {
            ...w,
            value: newValue
        }
    },
    maxReversalLength: (inputLength)=>inputLength-1,
    additive: true
}

export const REMOVE_DUPLICATED_LETTERS: Mutator = {
    name: "Remove duplicated letters",
    weight: 2,
    redHerringWeight: 1,
    apply: (w)=>{
        const {value}  = w;
        const letterCounts = _.countBy(value, l=>l.value)
        const newValue = value.filter(l=>letterCounts[l.value] === 1)
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const {value}  = w;
        const letterCounts = _.countBy(value, l=>l.value)
        if( Object.values(letterCounts).some(count=>count > 1)){
            throw new ReversalError("Letters are already duplicated")
        }
        const newLetterValue = sample(LETTERS.filter(l=>!letterCounts[l]))
        const newValue = [...value]
        for(let i = 0; i < 2; i++){
            const newLetter: Letter = {
                value: newLetterValue,
                key: -Math.random(),
                correctValue: null
            }
            newValue.splice(Math.floor(Math.random()*newValue.length), 0, newLetter)
        }
        return {
            ...w,
            value: newValue
        }
    },
    additive: false,
    maxReversalLength: (inputLength)=>inputLength+2,
};

export const DELETE_EVEN_LETTERS: Mutator = {
    name: "Remove even letters",
    weight: 2,
    redHerringWeight: 0.25,
    apply: (w:Word)=>{
        const {value}  = w;
        const newValue = value.filter((l:Letter, i:number)=>i % 2 === 0)
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const {value}  = w;
        const newValue = value.flatMap((l:Letter)=>{
            const newLetterValue = sample(LETTERS)
            const newLetter: Letter = {
                value: newLetterValue,
                key: -Math.random(),
                correctValue: null
            }
            return [l, newLetter]
        })
        return {
            ...w,
            value: newValue
        }
    },
    additive: false,
    maxReversalLength: (inputLength)=>inputLength,
};

export const DUPLICATE_LAST_LETTER: Mutator = {
    name: "Duplicate last letter",
    weight: 3,
    redHerringWeight: 1,
    apply: (w)=>{
        const {value}  = w;
        const newLetter: Letter = {
            value: value[value.length-1].value,
            key: "DUPLICATE_LAST_LETTER",
            correctValue: value[value.length-1].correctValue
        }
        const newValue = [...value, newLetter]
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const {value}  = w;
        const newValue = [...value]
        const removedLetter = newValue.pop()
        if(removedLetter?.value !== value[value.length-1].value){
            throw new ReversalError("Last letter is not the same as the 2nd last letter")
        }
        return {
            ...w,
            value: newValue
        }
    },
    additive: true,
    maxReversalLength: (inputLength)=>inputLength-1,
};

export const MOVE_VOWELS_TO_END: Mutator = {
    name: "Move vowels to end",
    weight: 2,
    redHerringWeight: 0.25,
    apply: (w)=>{
        const {value}  = w;
        const vowels = value.filter((l:Letter)=>VOWELS.includes(l.value))
        const newValue = value.filter((l:Letter)=>!VOWELS.includes(l.value)).concat(vowels)
        return {
            ...w,
            value: newValue
        }
    },
    reverse: (w)=>{
        const {value}  = w;
        /* throw error if the vowels are not at the end */
        let seenVowel = false;
        for(let i = 0; i < value.length; i++){
           const letterValue = value[i].value;
           const isVowel = VOWELS.includes(letterValue);
           if(isVowel && !seenVowel){
               seenVowel = true;
           }else if(!isVowel && seenVowel){
            throw new ReversalError("Vowels are not at the end")
            }
        }
        const vowels = value.filter((l:Letter)=>VOWELS.includes(l.value))
        const newValue = value.filter((l:Letter)=>!VOWELS.includes(l.value))
        /* Maintain the order of vowels while distributing them */
        const insertIndices = Array.from({length: vowels.length}, (_, i) => Math.floor(Math.random() * (newValue.length + 1 + i))).sort((a, b) => a - b);
        for (let i = 0; i < vowels.length; i++){
            newValue.splice(insertIndices[i], 0, vowels[i]);
        }
        return {
            ...w,
            value: newValue
        }
        
    },
    additive: true,
    maxReversalLength: (inputLength)=>inputLength,
};

function getAllMutators(): Mutator[] {
    return [
        CUT_IN_HALF,
        REMOVE_DUPLICATED_LETTERS,
        DUPLICATE_MIDDLE_LETTER,
        SORT_VOWELS,
        DELETE_EVEN_LETTERS,
        DUPLICATE_LAST_LETTER,
        MOVE_VOWELS_TO_END,
        ...ADD_LETTER_TO_MIDDLE,
        ...REMOVE_LETTER_AT_INDEX,
        ...REPLACE_VOWEL,
        ...REPLACE_ALL_VOWELS_WITH,
        DUPLICATE_FIRST_VOWEL,
        DELETE_FIRST_VOWEL,
        DELETE_FIRST_LETTER,
        DELETE_LAST_LETTER,
        ...ADD_COMMON_FRAGMENT_TO_END, 
        ...DELETE_COMMON_FRAGMENTS, 
        ...ADD_LETTER_AFTER_FIRST_VOWEL, 
        REVERSE, 
        ...ADD_LETTER_TO_END, 
        ...ADD_LETTER_TO_START,
        ...DELETE_LETTERS,
        SWAP_FIRST_AND_LAST
    ]
}

export function getRandomMutator({useRedHerringWeights}: {useRedHerringWeights: boolean}): Mutator {
    const all = getAllMutators()
    const weights = useRedHerringWeights ? all.map(m=>m.redHerringWeight) : all.map(m=>m.weight)
    return weighted_random(all, weights)
}

function willDeleteInformation(mutator:Mutator, word:Word): boolean {
    try{
        const reversed = mutator.reverse(word)
        const unreversed = mutator.apply(reversed)
        const inputString = word.value.map(l=>l.value).join('')
        const unreverseString = unreversed.value.map(l=>l.value).join('')
        return inputString !== unreverseString
    }catch(e:any){
        if (e.name === "ReversalError"){
            return true
        }
        throw e
    }
}

export function willReversingDoAnything(mutator:Mutator, word:Word): boolean {
    try{
        const reversed = mutator.reverse(word)
        const reversedString = reversed.value.map(l=>l.value).join('')
        const inputString = word.value.map(l=>l.value).join('')
        return inputString !== reversedString
    }catch(e:any){
        if (e.name === "ReversalError"){
            return false
        }
        throw e
    }
}
export function willDoAnything(mutator:Mutator, word:Word): boolean {
    const applied = mutator.apply(word)
    const appliedString = applied.value.map(l=>l.value).join('')
    const inputString = word.value.map(l=>l.value).join('')
    return inputString !== appliedString
}

export function isValidMutatorFor(mutator:Mutator, word:Word): boolean {
    try{
        const reversed = mutator.reverse(word)
        if(reversed.value.length > MAX_WORD_LENGTH){
            return false
        }
        const inputString = word.value.map(l=>l.value).join('')
        const reversedString = reversed.value.map(l=>l.value).join('')
        if (inputString === reversedString){
            return false
        }
    }catch(e:any){
        if (e.name === "ReversalError"){
            return false
        }
        throw e
    }
    return !willDeleteInformation(mutator, word)
}


export default getAllMutators
